import React from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { Icons } from '@model/common/icons';
import { getHotelOnlySearch } from '@state/slices/deal-finder/dealFinderSelectors';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';
import { ToolTip } from '@components/common/tooltip';
import { Icon } from '@components/common/generic-icon';

/* ***************** *
 *       Types       *
 * ***************** */
export interface WhatsIncludedProps {
  theme?: Theme;
  testId?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  display: 'none',
  [mq.medium]: {
    display: 'block'
  }
}));

const Header = styled.p(({ theme }: ThemeProps) => ({
  fontWeight: 500,
  marginTop: theme.custom.spacing.large,
  marginBottom: theme.custom.spacing.xSmall
}));

const Item = styled.div(({ theme }: ThemeProps) => ({
  marginTop: 0,
  marginBottom: theme.custom.spacing.xSmall
}));

const StyledIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group5.base,
  fontSize: '1.6rem',
  marginRight: theme.custom.spacing.xSmall
}));

const Info = styled(Icon)(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group1.base,
  cursor: 'pointer',
  fontSize: '1.4rem',
  marginLeft: theme.custom.spacing.xSmall
}));

const PriceTooltip = styled.div({
  maxWidth: 400
});

/* *********************** *
 *    WhatsIncludedComponent    *
 * *********************** */

export const WhatsIncludedComponent = (props: WhatsIncludedProps) => {
  const isHotelOnly = useSelector(getHotelOnlySearch);
  const t: any = useI18NextContext();

  return (
    <Container data-testid={props.testId || TestId.whatsIncluded.basket}>
      <Header>{t('whats-included-basket--header')}</Header>
      {!isHotelOnly && (
        <Item>
          <StyledIcon name={Icons.CHECK} />
          {t('whats-included-basket--return-flights')}
        </Item>
      )}
      <Item>
        <StyledIcon name={Icons.CHECK} />
        {t('whats-included-basket--hotel-accommodation')}
      </Item>
      <Item>
        <StyledIcon name={Icons.CHECK} />
        {t('whats-included-basket--abta-protection')}
      </Item>
      <Item>
        <StyledIcon name={Icons.CHECK} />
        {t('whats-included-basket--holiday-rep')}
      </Item>
      <Item>
        <ToolTip
          content={<PriceTooltip>{t('whats-included-basket--price-tooltip')}</PriceTooltip>}
          direction={'down'}
          distance={10}
        >
          <span>
            <StyledIcon name={Icons.CHECK} />
            {t('whats-included-basket--price-guarantee')}
            <Info name={Icons.INFO} />
          </span>
        </ToolTip>
      </Item>
    </Container>
  );
};

export const WhatsIncluded = withTheme(WhatsIncludedComponent);
